import React from "react";
import ModalWrapper from "./ModalWrapper";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";

interface Announcement {
  id: number;
  title: string;
  body?: string;
  image?: string;
  cta1_label?: string;
  cta1_url?: string;
  cta2_label?: string;
  cta2_url?: string;
  close_label?: string;
  allow_snooze?: boolean;
}

interface AnnouncementDialogProps {
  announcement: Announcement;
  onClose: () => void;
  onDismiss: () => Promise<void>;
  onSnooze: () => Promise<void>;
  onCtaClick: (cta: 1 | 2) => Promise<void>;
}

const AnnouncementDialog: React.FC<AnnouncementDialogProps> = ({
  announcement,
  onClose,
  onDismiss,
  onSnooze,
  onCtaClick,
}) => {
  const navigate = useNavigate();
  const handleClose = async () => {
    await onDismiss();
    onClose();
  };
  const handleNavigate = (url: string, inApp: boolean) => {
    if (inApp) {
      navigate(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const bodyLength = announcement.body?.length ?? 0;
  const modalSize =
    announcement.image || bodyLength > 400
      ? "lg"
      : bodyLength > 180
      ? "md"
      : "sm";

  return (
    <ModalWrapper
      onClose={handleClose}
      title={announcement.title}
      size={modalSize}
      footer={
        <div className="flex flex-wrap items-center gap-3 w-full">
          {announcement.allow_snooze !== false && (
            <button
              onClick={async () => {
                await onSnooze();
                onClose();
              }}
              className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69 text-sm px-3 py-1.5"
            >
              Später erinnern
            </button>
          )}

          <div className="flex flex-wrap justify-end gap-2 ml-auto">
            {announcement.cta2_label && announcement.cta2_url && (
              <button
                type="button"
                onClick={async () => {
                  await onCtaClick(2);
                  const url = announcement.cta2_url as string;
                  const inApp = url.startsWith("/");
                  if (inApp) {
                    await onDismiss();
                  }
                  handleNavigate(url, inApp);
                  if (inApp) {
                    onClose();
                  }
                }}
                className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69"
              >
                {announcement.cta2_label}
              </button>
            )}

            {announcement.cta1_label && announcement.cta1_url && (
              <button
                type="button"
                onClick={async () => {
                  await onCtaClick(1);
                  const url = announcement.cta1_url as string;
                  const inApp = url.startsWith("/");
                  if (inApp) {
                    await onDismiss();
                  }
                  handleNavigate(url, inApp);
                  if (inApp) {
                    onClose();
                  }
                }}
                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
              >
                {announcement.cta1_label}
              </button>
            )}

            {announcement.close_label && (
              <button
                onClick={handleClose}
                className="_button_19rto_40 _secondary_19rto_163 _outlined_19rto_69"
              >
                {announcement.close_label}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Inhalt */}
      <div className="flex flex-col gap-6">
        {announcement.image && (
          <img
            src={announcement.image}
            alt=""
            className="w-full rounded-lg max-h-40 object-contain"
          />
        )}

        {announcement.body && (
          <div className="prose max-w-none">
            <ReactMarkdown>{announcement.body}</ReactMarkdown>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default AnnouncementDialog;
